import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Shield, Vault, CreditCard, Calendar, Heart, X, ArrowRight, ArrowLeft } from "lucide-react";

const STEPS = [
  {
    icon: Shield,
    title: "Welcome to Withdraw Guard",
    body: "Money is hard to keep when it's one swipe away. We help you put it just out of reach — so the future you actually arrives.",
    visual: "shield",
  },
  {
    icon: Vault,
    title: "Two protective vaults",
    body: "Untouchable Retirement is locked until age 60. Emergency Job-Loss Savings is locked until you upload verified proof of job loss — our AI reviews the document and only then unlocks the money.",
    visual: "vaults",
  },
  {
    icon: CreditCard,
    title: "Three accounts, two cards",
    body: "Real bank accounts: Spending, Savings, Bills. Each card (on Spending + Bills) has its own merchant-category rules. Savings has no card on purpose.",
    visual: "cards",
  },
  {
    icon: Calendar,
    title: "Payday auto-split",
    body: "When payroll lands, we move a slice to Bills and another slice into a savings vault automatically. Splits lock for 3 days before payday so you can't game your future self.",
    visual: "split",
  },
  {
    icon: Heart,
    title: "Plan for the long game",
    body: "Add Beneficiaries, open a Baby Fund locked until your child's 18th birthday, or set time-locks for short-term goals. Got a question? Tap Support and we'll respond.",
    visual: "long",
  },
];

export default function WalkthroughModal() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Don't show walkthrough until DOB has been set — DobGate takes priority.
    if (user && user.paid && user.date_of_birth && user.walkthrough_completed === false) {
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [user]);

  if (!open) return null;

  const finish = async () => {
    try { await api.post("/auth/walkthrough/complete"); } catch { /* ignore */ }
    setOpen(false);
    if (refresh) await refresh();
  };

  const S = STEPS[step];
  const Icon = S.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm" data-testid="walkthrough-modal">
      <div className="relative bg-cream rounded-3xl border border-sand max-w-lg w-full overflow-hidden shadow-2xl">
        {/* Progress bar */}
        <div className="flex gap-1.5 p-4 pb-0">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-forest" : "bg-sand"}`}
                 data-testid={`walkthrough-progress-${i}`} />
          ))}
        </div>

        <button onClick={finish} aria-label="Close walkthrough"
          className="absolute top-3 right-3 w-10 h-10 inline-flex items-center justify-center text-sage hover:text-ink active:bg-linen rounded-lg transition-colors z-10"
          data-testid="walkthrough-close">
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 sm:p-10" data-testid={`walkthrough-step-${step}`}>
          <div className="w-14 h-14 rounded-2xl bg-forest/10 flex items-center justify-center">
            <Icon className="w-7 h-7 text-forest" strokeWidth={1.5} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage mt-5">
            Step {step + 1} of {STEPS.length}
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-2">{S.title}</h2>
          <p className="text-moss mt-3 leading-relaxed" data-testid={`walkthrough-body-${step}`}>{S.body}</p>
        </div>

        <div className="flex items-center justify-between gap-2 px-8 pb-8 sm:px-10 sm:pb-10">
          {step > 0 ? (
            <button onClick={() => setStep(s => s - 1)}
              className="inline-flex items-center gap-1.5 text-sm text-moss hover:text-ink transition-colors px-3 py-2"
              data-testid="walkthrough-back-btn">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          ) : (
            <button onClick={finish}
              className="text-sm text-sage hover:text-ink transition-colors px-3 py-2"
              data-testid="walkthrough-skip-btn">
              Skip tour
            </button>
          )}
          {isLast ? (
            <button onClick={finish}
              className="inline-flex items-center gap-2 bg-forest text-white rounded-xl px-6 py-3 text-sm font-medium hover:bg-forest-deep transition-colors"
              data-testid="walkthrough-finish-btn">
              Get started <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={() => setStep(s => s + 1)}
              className="inline-flex items-center gap-2 bg-forest text-white rounded-xl px-6 py-3 text-sm font-medium hover:bg-forest-deep transition-colors"
              data-testid="walkthrough-next-btn">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
